import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo, useEffect } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import ShopperTextEditor from 'components/TextEditor';

import useDialog from 'hooks/useDialog';
import usePendingComments from 'hooks/usePendingComments';
import useToast from 'hooks/useToast';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import { useDomain } from 'providers/DomainProvider';
import { useUser } from 'providers/UserProvider';

import { useCommentsRoot } from '../Root';
import UserAvatar from './UserAvatar';

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const TextEditor = ({
  aliasId,
  answering,
  children,
  className,
  commentId,
  commentStars,
  customPlaceholder,
  editing = false,
  initialValue,
  parentId,
  onCancel,
  onCreateSuccess,
  onSend,
}) => {
  const { showToast } = useToast();
  const { showDialog } = useDialog();
  const { domain, domainId, domainIdKey } = useDomain();
  const {
    createPendingComment,
    deletePendingCommentsFromDomain,
    getPendingCommentsFromDomain,
  } = usePendingComments();
  const {
    activeAnswer,
    gaEventCategory,
    createComment,
    editComment,
    setActiveAnswer,
  } = useCommentsRoot();
  const { loadingLoggedInUser, isLoggedIn } = useUser();

  /*
    Quando há `activeAnswer` no contexto (usuário clicou em
    "Responder" em algum comentário da lista), esse textbox único
    passa a atuar como reply: o `answering` vira a @username do alvo
    e o `parentId` aponta pro comentário raiz da thread. Se quem
    instanciou já tiver passado esses props explicitamente (ex.: box
    de edição), mantemos a intenção do caller e ignoramos o context.
  */
  const effectiveAnswering =
    answering ?? (!editing ? activeAnswer?.userUsername : undefined);
  const effectiveParentId =
    parentId ?? (!editing ? activeAnswer?.parentId : undefined);

  useEffect(() => {
    const createRecoveredComment = () => {
      if (!isLoggedIn) {
        return;
      }

      const pendingComments = getPendingCommentsFromDomain(domain, domainId);

      if (pendingComments.length === 0) {
        return;
      }

      pendingComments.map(({ comment }) => {
        createComment(comment);
      });
      deletePendingCommentsFromDomain(domain, domainId);
      showToast({
        text: placeholder('toasts.successes.comments.created'),
        type: 'success',
      });
    };

    createRecoveredComment();
  }, [isLoggedIn]);

  const onFocus = () => {
    sendEvent({
      action: isLoggedIn ? 'textbox_click_logged' : 'textbox_click_unlogged',
      category: gaEventCategory,
    });
  };

  const onClose = () => {
    sendEvent({ category: gaEventCategory, action: 'close' });
  };

  const onSendHandler = (commentText) => {
    if (editing) {
      editComment({ commentId, commentText, parentId });

      if (onSend) {
        onSend();
      }
      return;
    }

    const data = {
      aliasId,
      commentStars,
      commentText,
      parentId: effectiveParentId,
    };

    if (!isLoggedIn) {
      /**
       * Unfortunately for technical reasons we can't consider that the created
       * comment after login is visually considered a reply of a review.
       */
      createPendingComment({
        comment: data,
        domain,
        domainId,
        domainIdKey,
      });
      showDialog(LoginDialog);
      return;
    }

    createComment({ ...data, onSuccess: onCreateSuccess });

    if (onSend) {
      onSend();
    }
  };

  if (loadingLoggedInUser) {
    return null;
  }

  /*
    Permite sair do modo "respondendo" sem precisar enviar —
    encaminha pro caller quando veio por prop (ex.: modo edição) e,
    quando o estado de resposta veio do `activeAnswer` no contexto,
    limpa o contexto.
  */
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (!editing && activeAnswer) {
      setActiveAnswer(null);
    }
  };

  return (
    <div
      className={twMerge(
        'grid grid-cols-[auto_1fr] gap-2 md:gap-4',
        !editing && effectiveAnswering && 'pb-1 pr-2 pt-1 md:pl-10',
        className
      )}
    >
      {!editing && <UserAvatar />}
      <ShopperTextEditor
        answering={!editing ? effectiveAnswering : undefined}
        className={twJoin(
          'col-span-3',
          editing ? 'col-start-1' : 'col-start-2'
        )}
        gaEventCategory={gaEventCategory}
        initialValue={initialValue}
        placeholder={
          effectiveAnswering
            ? placeholder('inputs.placeholders.answering')
            : customPlaceholder || placeholder('inputs.placeholders.comment')
        }
        onCancel={handleCancel}
        onClose={onClose}
        onFocus={onFocus}
        onSend={onSendHandler}
      >
        {children && (
          <div className="col-span-4 col-start-1 flex flex-col gap-y-4">
            {children}
          </div>
        )}
      </ShopperTextEditor>
    </div>
  );
};

TextEditor.propTypes = {
  aliasId: PropTypes.number,
  answering: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  commentId: PropTypes.number,
  /**
   * Nota de avaliação (1-5). Quando passado, o comentário é enviado
   * como review (o backend interpreta `commentStars` ao criar o comment).
   */
  commentStars: PropTypes.number,
  /**
   * Override opcional do placeholder do campo. Ignorado quando `answering`
   * está definido. Se omitido, usa a key padrão `inputs.placeholders.comment`.
   */
  customPlaceholder: PropTypes.string,
  editing: PropTypes.bool,
  initialValue: PropTypes.string,
  parentId: PropTypes.number,
  onCancel: PropTypes.func,
  /**
   * Callback executado somente após o backend confirmar a criação do
   * comentário (disparado pelo `onSuccess` da mutation em Comments.Root).
   * Use para limpar estado UI dependente do sucesso (ex.: seleção de
   * estrelas em aba de avaliações) — o `onSend` dispara já no submit e
   * não deve ser usado para isso.
   */
  onCreateSuccess: PropTypes.func,
  onSend: PropTypes.func,
};

export default memo(TextEditor);
